import { CapitalEventPageWrapper } from "features/capital-activity/CapitalActivityPageWrapper";
import { waterfallRoutes } from "features/waterfall/WaterfallRoutes";
import {
  getCapitalActivityEventRetrieveLoader,
  getCapitalActivityInvestorOverviewRetrieveLoader,
  getCapitalActivitySummaryRetrieveLoader,
} from "gen/endpoints/capital-activity/capital-activity";
import { getCapitalCallReadOnlyDrawdownTableRetrieveLoader } from "gen/endpoints/capital-call/capital-call";
import {
  getFundFamilyEventRetrieveLoader,
  getFundFamilySubEventListLoader,
  getNoticeDocumentGenerationStatusRetrieveLoader,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { getNoticeConfigRetrieveLoader } from "gen/endpoints/notice-config/notice-config";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const capitalActivityRoutes: RouteObject = {
  id: "capitalActivity",
  path: "capital-activity",
  children: [
    {
      id: "capitalActivityIndex",
      index: true,
      handle: {
        showSidebar: true,
      },
      loader: getCapitalActivitySummaryRetrieveLoader(),
      lazy: async () => {
        const { CapitalActivityPage } = await import(
          "features/capital-activity/CapitalActivityPage"
        );
        return { Component: CapitalActivityPage };
      },
    },
    {
      id: "capitalActivityCapitalActivityEvent",
      path: ":fundFamilyEventId",
      element: <CapitalEventPageWrapper />,
      children: [
        {
          id: "capitalActivityCapitalActivityEventIndex",
          path: "",
          index: true,
          lazy: async () => {
            const { CapitalEventPage } = await import(
              "features/capital-activity/event/CapitalEventPage"
            );
            return { Component: CapitalEventPage };
          },
          loader: composeLoaders(
            getFundFamilyEventRetrieveLoader(),
            getCapitalActivityEventRetrieveLoader(),
            getFundFamilySubEventListLoader(),
            getNoticeDocumentGenerationStatusRetrieveLoader(),
            getCapitalActivityInvestorOverviewRetrieveLoader({}),
            getNoticeConfigRetrieveLoader(),
          ),
        },
        {
          id: "capitalActivityCapitalActivityEventNotices",
          path: "notices",
          lazy: async () => {
            const { CapitalEventNoticePage } = await import(
              "features/capital-activity/event/CapitalEventNoticePage"
            );
            return { Component: CapitalEventNoticePage };
          },
          handle: {
            title: i18n.t("capitalActivity:notices"),
          },
        },
        {
          id: "capitalActivityCapitalActivityEventCalls",
          path: "calls",
          lazy: async () => {
            const { CapitalCallPage } = await import(
              "features/capital-call/CapitalCallPage"
            );
            return { Component: CapitalCallPage };
          },
          loader: composeLoaders(
            getFundFamilyEventRetrieveLoader(),
            getCapitalCallReadOnlyDrawdownTableRetrieveLoader(),
            getNoticeDocumentGenerationStatusRetrieveLoader(),
            getNoticeConfigRetrieveLoader(),
          ),
          children: [
            {
              id: "capitalActivityCapitalActivityEventAllocation",
              index: true,
              path: "",
              lazy: async () => {
                const { CapitalCallAllocationPage } = await import(
                  "features/capital-call/CapitalCallAllocationPage"
                );
                return { Component: CapitalCallAllocationPage };
              },
              handle: {
                title: i18n.t("capitalCall:allocationOfCall"),
              },
            },
            {
              id: "capitalActivityCapitalActivityEventBreakdown",
              path: "breakdown",
              lazy: async () => {
                const { CapitalCallBreakdownPage } = await import(
                  "features/capital-call/CapitalCallBreakdownPage"
                );
                return { Component: CapitalCallBreakdownPage };
              },
              handle: {
                title: i18n.t("capitalCall:investorBreakdown"),
              },
            },
            {
              id: "feeBasisNotification",
              path: "fee-basis-notification",
              lazy: async () => {
                const { FeeBasisNotification } = await import(
                  "features/capital-call/FeeBasisNotification"
                );
                return { Component: FeeBasisNotification };
              },
              handle: {
                title: i18n.t("capitalCall:feeBasisNotification.handle"),
              },
            },
            {
              id: "capitalActivityCapitalActivityCallReallocation",
              handle: {
                title: i18n.t("capitalCall:reallocation.updatePage.title"),
                breadcrumb: i18n.t(
                  "capitalCall:reallocation.updatePage.breadcrumb",
                ),
              },
              path: "reallocation/:reallocationFundFamilyEventId",
              lazy: async () => {
                const { CapitalEventReallocationPage } = await import(
                  "features/capital-activity/event/reallocation/CapitalEventReallocationPage"
                );
                return { Component: CapitalEventReallocationPage };
              },
              children: [
                {
                  id: "capitalActivityCapitalActivityEventReallocationUpdate",
                  path: "",
                  index: true,
                  lazy: async () => {
                    const { CapitalEventReallocationUpdatePage } = await import(
                      "features/capital-activity/event/reallocation/CapitalEventReallocationUpdatePage"
                    );
                    return { Component: CapitalEventReallocationUpdatePage };
                  },
                  handle: {
                    title: i18n.t("capitalCall:reallocation.updatePage.title"),
                    breadcrumb: i18n.t(
                      "capitalCall:reallocation.updatePage.breadcrumb",
                    ),
                  },
                },
                {
                  id: "capitalActivityCapitalActivityEventReallocationReview",
                  path: "review",
                  lazy: async () => {
                    const { CapitalCallBreakdownPage } = await import(
                      "features/capital-call/CapitalCallBreakdownPage"
                    );
                    return { Component: CapitalCallBreakdownPage };
                  },
                  handle: {
                    title: i18n.t("capitalCall:reallocation.reviewPage.title"),
                    breadcrumb: i18n.t(
                      "capitalCall:reallocation.reviewPage.breadcrumb",
                    ),
                  },
                },
              ],
            },
          ],
        },
        {
          id: "capitalActivityCapitalActivityEventDistributions",
          path: "distribution",
          children: [
            {
              id: "capitalActivityEventDistributions",
              path: "distributions",
              lazy: async () => {
                const { FundFamilyDistributionPage } = await import(
                  "features/distributions/FundFamilyDistributionPage"
                );
                return { Component: FundFamilyDistributionPage };
              },
              loader: composeLoaders(getFundFamilyEventRetrieveLoader()),
              children: [
                {
                  id: "capitalActivityEventDistributionAllocation",
                  index: true,
                  path: "",
                  lazy: async () => {
                    const { FundFamilyDistributionAllocationPage } =
                      await import(
                        "features/distributions/FundFamilyDistributionAllocationPage"
                      );
                    return { Component: FundFamilyDistributionAllocationPage };
                  },
                  handle: {
                    title: i18n.t(
                      "distribution:allocationOfDistribution.title",
                    ),
                    breadcrumb: i18n.t(
                      "distribution:allocationOfDistribution.breadcrumb",
                    ),
                  },
                },
                {
                  id: "capitalActivityEventDistributionBreakdown",
                  path: "breakdown",
                  lazy: async () => {
                    const { FundFamilyDistributionBreakdownPage } =
                      await import(
                        "features/distributions/FundFamilyDistributionBreakdownPage"
                      );
                    return { Component: FundFamilyDistributionBreakdownPage };
                  },
                  handle: {
                    title: i18n.t("distribution:investorBreakdown.title"),
                    breadcrumb: i18n.t(
                      "distribution:investorBreakdown.breadcrumb",
                    ),
                  },
                },
              ],
            },
          ],
        },
        {
          id: "capitalActivityCapitalActivityEventEqualization",
          path: "equalization",
          children: [
            {
              id: "capitalActivityCapitalActivityEventEqualizationView",
              path: ":fundFamilySubEventId",
              lazy: async () => {
                const { EqualizationPage } = await import(
                  "features/equalization/EqualizationPage"
                );
                return { Component: EqualizationPage };
              },
            },
          ],
        },
        waterfallRoutes,
      ],
    },
  ],
};
